// @ts-check
// goalService.js
// Purpose: Handles all CRUD operations and cache logic for goals in Supabase.
// Features: PERMANENT CACHE + INCREMENTAL SYNC for optimal performance and cost efficiency
//
// CACHING STRATEGY OVERVIEW:
// ========================
// Traditional Approach (OLD): Cache expires every 5 minutes → Full refetch of ALL goals
// New Approach (IMPLEMENTED): Permanent cache + incremental sync for new/updated goals only
//
// BENEFITS:
// - Instant loading: Goals always available from cache
// - Cost efficient: Only fetch new/changed goals since last sync
// - Bandwidth friendly: Minimal data transfer
// - Offline resilient: Works with cached data when offline
// - Real-time feel: Background sync keeps data fresh
//
// HOW IT WORKS:
// 1. First load: Full fetch → Store in permanent cache with sync timestamp
// 2. Subsequent loads: Return cached data instantly → Background incremental sync
// 3. Incremental sync: Only fetch goals newer/updated since lastSync timestamp
// 4. New data creation: Add directly to cache (no clearing)
// 5. Fallback: Full refresh if incremental sync fails repeatedly
//
// Inputs: userId, goal data, cacheManager, supabase

import { supabase } from '../../config/supabase';
import { getAppSessionId, getUuidSessionId } from '../../utils/activityEvents';
// Use centralized cacheManager to avoid duplicate instances. Prefer this path across services.
import logger from '../../utils/logger';
import { cacheManager } from '../cacheManager';
import { GoalSchema } from '../../lib/schemas/goal';
// Centralized logger for consistent, level-based logging across services

// Utility: detect when a Supabase query was aborted by an AbortController.
// Supabase (cross-fetch) often uses DOMException with name 'AbortError' and code '20'.
/**
 * @param {any} err
 * @returns {boolean}
 */
function isAbortError(err) {
  const name = /** @type {any} */ (err)?.name || '';
  const code = String(/** @type {any} */ (err)?.code ?? '');
  const message = String(/** @type {any} */ (err)?.message ?? '');
  return name === 'AbortError' || code === '20' || /AbortError/i.test(message);
}

/**
 * Create a new version of an existing goal (v2+)
 *
 * Behavior per docs:
 * - Set: goal_lineage from current row's lineage (or id if lineage is null), user_id,
 *   and content fields being changed. Optionally goal_change_notes.
 * - Leave unset: goal_version, previous_goal_version, is_current (triggers handle these).
 * - We also pre-seed unspecified content fields with the previous version's values so the
 *   new row faithfully represents the edited copy.
 *
 * @param {string} userId
 * @param {any} prevGoal - The current (is_current) goal row being versioned
 * @param {Record<string, any>} updates - The fields changed by the user
 * @returns {Promise<any>} The newly created goal row (new version)
 */
export async function createGoalVersion(userId, prevGoal, updates) {
  logger.info('🎯 goalService.createGoalVersion: Start', { userId, prevId: prevGoal?.id });
  if (!userId || !prevGoal?.id) {
    throw new Error('Missing userId or previous goal row');
  }

  // Define the content fields we want to carry forward by default.
  /** @type {string[]} */
  const contentKeys = [
    'title',
    'specific',
    // Spelling: only use canonical 'measurable'
    'measurable',
    'achievable',
    'relevant',
    'time_bound',
    'is_active',
    'priority',
    // Persist the assigned color across versions
    'color_key',
    // Preserve user's drag-and-drop ordering across versions
    'importance_rank',
    'smart',
    'smartified_goal_text',
    'goal_change_notes',
    // Progress tracking fields (added 2025-10-15)
    'baseline_value',
    'baseline_unit',
    'target_value',
    'target_unit',
  ];

  // Start with previous values, overlay updates.
  /** @type {Record<string, any>} */
  const merged = {};
  for (const k of contentKeys) {
    const key = String(k);
    if (
      Object.prototype.hasOwnProperty.call(prevGoal, key) &&
      typeof prevGoal[key] !== 'undefined'
    ) {
      merged[key] = prevGoal[key];
    }
  }
  for (const [k, v] of Object.entries(updates || {})) merged[k] = v;

  // Normalize spelling: always remove legacy 'measureable' and prefer 'measurable'
  if (Object.prototype.hasOwnProperty.call(merged, 'measureable')) {
    if (typeof merged.measurable === 'undefined') merged.measurable = merged.measureable;
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete merged.measureable;
  }

  /** @type {Record<string, any>} */
  const insertPayload = {
    user_id: userId,
    goal_lineage: prevGoal.goal_lineage || prevGoal.id,
    ...merged,
  };

  // Final safety: remove any stray legacy key before insert
  if (Object.prototype.hasOwnProperty.call(insertPayload, 'measureable')) {
    if (typeof (/** @type {any} */ (insertPayload).measurable) === 'undefined')
      /** @type {any} */ (insertPayload).measurable = /** @type {any} */ (
        insertPayload
      ).measureable;
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete (/** @type {any} */ (insertPayload).measureable);
  }
  let { data, error } = await supabase.from('goals').insert([insertPayload]).select().single();
  if (error) {
    // Retry with legacy key name if the project still uses `measureable`
    const msg = String(error?.message || '');
    const code = String(error?.code || '');
    const missingMeasurable =
      code === '42703' ||
      code === 'PGRST204' ||
      /column\s+"?measurable"?\s+does not exist/i.test(msg) ||
      /Could not find the 'measurable' column of 'goals' in the schema cache/i.test(msg);
    if (missingMeasurable && 'measurable' in insertPayload) {
      /** @type {Record<string, any>} */
      const fallback = { ...insertPayload };
      fallback.measureable = fallback.measurable;
      delete fallback.measurable;
      ({ data, error } = await supabase.from('goals').insert([fallback]).select().single());
    }
  }
  if (error) {
    logger.error('❌ goalService.createGoalVersion: Supabase error:', error);
    throw error;
  }

  // Reassign children (tasks, plans) from old goal to new goal, recording provenance.
  // This runs best-effort and logs errors without failing the main operation.
  try {
    await reassignChildrenToNewGoal(userId, prevGoal, data);
  } catch (e) {
    logger.warn('⚠️ goalService.createGoalVersion: Child reassignment encountered issues:', e);
  }

  // Merge into permanent cache as a new current version
  const cacheKey = 'goals';
  const syncTimestamp = new Date().toISOString();
  {
    const res = GoalSchema.safeParse(data);
    if (!res.success) {
      logger.warn('goalService.createGoal: Goal validation failed (non-blocking)', {
        id: /** @type {any} */ (data)?.id,
        issues: res.error.issues?.slice(0, 3),
      });
      cacheManager.mergeIncremental(cacheKey, [data], userId, syncTimestamp);
    } else {
      cacheManager.mergeIncremental(cacheKey, [res.data], userId, syncTimestamp);
    }
  }
  logger.info('✅ goalService.createGoalVersion: Created new version', { id: data.id });
  return data;
}

/**
 * Reassign tasks and plans linked to prevGoal to the new goal row while recording provenance.
 * - Tasks: set origin_goal_* if null, then update linked_goal_id to new goal id.
 * - Plans: merge origin info into plan_json if absent, then update linked_goal_id.
 *
 * Provenance fields recorded:
 * - origin_goal_id
 * - origin_goal_lineage
 * - origin_goal_version (if present on prevGoal)
 *
 * Errors are logged and do not throw to avoid breaking the main flow.
 * @param {string} userId
 * @param {any} prevGoal
 * @param {any} newGoal
 */
async function reassignChildrenToNewGoal(userId, prevGoal, newGoal) {
  const oldGoalId = /** @type {any} */ (prevGoal)?.id;
  const newGoalId = /** @type {any} */ (newGoal)?.id;
  if (!userId || !oldGoalId || !newGoalId) return;

  const lineage = /** @type {any} */ (prevGoal)?.goal_lineage || oldGoalId;
  const version = /** @type {any} */ (prevGoal)?.goal_version ?? null;

  // 1) Tasks: record origin if null, then relink to new goal
  try {
    // First, set origin fields where they are currently null for tasks still linked to oldGoalId
    // Note: PostgREST cannot express COALESCE in update payload; do it in two steps.
    const { error: tOriginErr } = await supabase
      .from('tasks')
      .update({
        origin_goal_id: oldGoalId,
        origin_goal_lineage: lineage,
        origin_goal_version: version,
      })
      .eq('user_id', userId)
      .eq('linked_goal_id', oldGoalId)
      .is('deleted_at', null)
      .is('origin_goal_id', null);
    if (tOriginErr) logger.warn('tasks provenance update failed (non-blocking):', tOriginErr);

    // Then, relink tasks to the new goal id
    const { error: tRelinkErr } = await supabase
      .from('tasks')
      .update({ linked_goal_id: newGoalId })
      .eq('user_id', userId)
      .eq('linked_goal_id', oldGoalId)
      .is('deleted_at', null);
    if (tRelinkErr) logger.warn('tasks relink failed (non-blocking):', tRelinkErr);
  } catch (e) {
    logger.warn('Task reassignment encountered error (non-blocking):', e);
  }

  // 2) Plans: record origin via columns if null, then relink to new goal
  try {
    const { error: pOriginErr } = await supabase
      .from('plans')
      .update({
        origin_goal_id: oldGoalId,
        origin_goal_lineage: lineage,
        origin_goal_version: version,
      })
      .eq('user_id', userId)
      .eq('linked_goal_id', oldGoalId)
      .eq('is_current', true)
      .is('deleted_at', null)
      .is('origin_goal_id', null);
    if (pOriginErr) logger.warn('plans provenance update failed (non-blocking):', pOriginErr);

    const { error: pRelinkErr } = await supabase
      .from('plans')
      .update({ linked_goal_id: newGoalId })
      .eq('user_id', userId)
      .eq('linked_goal_id', oldGoalId)
      .eq('is_current', true)
      .is('deleted_at', null);
    if (pRelinkErr) logger.warn('plans relink failed (non-blocking):', pRelinkErr);
  } catch (e) {
    logger.warn('Plan reassignment encountered error (non-blocking):', e);
  }
}
/**
 * Get all goals for a user with permanent cache and incremental sync
 *
 * PERMANENT CACHE STRATEGY:
 * ========================
 * This function implements a sophisticated caching strategy that eliminates the need for
 * repeated full data fetches, dramatically improving performance and reducing API costs.
 *
 * FLOW:
 * 1. Check permanent cache first - if data exists, return immediately (instant loading)
 * 2. Trigger background incremental sync to fetch only new/updated goals since last sync
 * 3. If no cache exists, perform full fetch and establish cache baseline
 * 4. Robust fallback: return cached data even if fresh fetch fails
 *
 * INCREMENTAL SYNC LOGIC:
 * - Uses lastSync timestamp to query only goals newer/updated than cache
 * - Merges new/updated goals with existing cache (no duplicates)
 * - Updates sync timestamp for next incremental fetch
 * - Falls back to full refresh if incremental sync fails repeatedly
 *
 * @param {string} userId - User ID
 * @param {{ forceRefresh?: boolean, signal?: AbortSignal, includeInvisible?: boolean }} [options] - Options including forceRefresh, abort signal, and includeInvisible
 * @returns {Promise<any[]>} Array of goals (normalized)
 */
export async function getGoals(
  userId,
  { forceRefresh = false, signal, includeInvisible = false } = {},
) {
  if (!userId) {
    logger.warn('⚠️ goalService.getGoals: Missing userId');
    return [];
  }

  const cacheKey = 'goals';
  logger.info('🎯 goalService.getGoals: Fetching goals for user:', userId, {
    forceRefresh,
    includeInvisible,
  });

  // Helper: apply UI visibility filtering before returning to callers.
  /**
   * @param {any[]} arr
   */
  const applyVisibility = (arr) => {
    const base = Array.isArray(arr)
      ? arr.filter((g) => Boolean(g && g.is_current === true && g.deleted_at === null))
      : [];
    if (includeInvisible) return base;
    return base.filter((g) => {
      const slug = String(/** @type {any} */ (g)?.system_slug || '').trim();
      const inv = Boolean(/** @type {any} */ (g)?.is_invisible === true);
      const title = String(/** @type {any} */ (g)?.title || '')
        .trim()
        .toLowerCase();
      const isChoresTitle = title === 'chores & errands' || title === 'chores and errands';
      // Hide system chores goal and any explicitly invisible rows by default
      // Fallback by title to handle older rows missing system_slug
      return slug !== 'chores_errands' && !inv && !isChoresTitle;
    });
  };

  // STEP 1: PERMANENT CACHE CHECK
  // =============================
  // If we have cached data, prefer it for instant UI, but avoid the "empty baseline" trap.
  if (!forceRefresh && cacheManager.hasData(cacheKey, userId)) {
    const cached = cacheManager.get(cacheKey, userId);
    const hasBaseline = Boolean(cacheManager.getLastSyncTimestamp(cacheKey, userId));
    if (cached && Array.isArray(cached)) {
      const filteredCached = applyVisibility(cached);
      if (filteredCached.length > 0 && hasBaseline) {
        logger.debug('✅ goalService.getGoals: Returning cached goals:', filteredCached.length);
        return filteredCached;
      }

      // If cache exists but is empty OR baseline missing, do a FULL FETCH now to establish baseline
      logger.info(
        '⚠️ goalService.getGoals: Cached goals are empty or missing baseline. Performing full fetch to establish cache. { cachedLength:',
        cached.length,
        ', hasBaseline:',
        hasBaseline,
        ' }',
      );
      try {
        const fresh = await performFullFetch(userId, cacheKey, signal);
        return applyVisibility(fresh);
      } catch (e) {
        if (isAbortError(e)) {
          logger.debug('⏭️ goalService.getGoals: Full fetch aborted during baseline establishment');
          // Return current cached (possibly empty) data quietly on abort
          return Array.isArray(cached) ? applyVisibility(cached) : [];
        }
        logger.error('❌ goalService.getGoals: Full fetch after empty cache failed:', e, {
          hasBaseline,
        });
        // As a last resort, return the empty cached array to avoid crashing the UI
        return [];
      }
    }
  }

  // STEP 2: FULL FETCH (NO CACHE OR FORCE REFRESH)
  // ==============================================
  // No cache exists or user explicitly requested fresh data
  // Perform full fetch to establish cache baseline
  try {
    logger.info('🔄 goalService.getGoals: Performing full fetch (no cache or force refresh)');
    const fresh = await performFullFetch(userId, cacheKey, signal);
    return applyVisibility(fresh);
  } catch (error) {
    if (isAbortError(error)) {
      logger.debug('⏭️ goalService.getGoals: Full fetch aborted');
      // On abort, prefer returning cached if available, otherwise empty array
      const fallbackCache = cacheManager.get(cacheKey, userId);
      return Array.isArray(fallbackCache) ? applyVisibility(fallbackCache) : [];
    }
    logger.error('❌ goalService.getGoals: Full fetch failed:', error, {
      hasBaseline: Boolean(cacheManager.getLastSyncTimestamp(cacheKey, userId)),
    });

    // FALLBACK STRATEGY
    // ================
    // If fresh fetch fails, return cached data if available (even if potentially stale)
    // This ensures app remains functional even with network issues
    const fallbackCache = cacheManager.get(cacheKey, userId);
    if (Array.isArray(fallbackCache)) {
      logger.info('🔄 goalService.getGoals: Returning fallback cached data:', fallbackCache.length);
      return applyVisibility(fallbackCache);
    }

    // No cache available, return empty array
    logger.warn('⚠️ goalService.getGoals: No cache available, returning empty array');
    return [];
  }
}

// Incremental sync removed: realtime + full fetch establishes and updates cache baseline
/**
 * ESTABLISHES CACHE BASELINE:
 * - Fetches ALL goals for the user
 * - Stores them in permanent cache
 * - Sets sync timestamp for future incremental syncs
 * - This becomes the foundation for all future incremental updates
 *
 * FULL FETCH STRATEGY:
 * ===================
 * This function is used when:
 * 1. No cache exists (first time loading goals)
 * 2. User explicitly requests fresh data (forceRefresh=true)
 * 3. Incremental sync has failed multiple times (fallback)
 *
 *
 * @param {string} userId - User ID
 * @param {string} cacheKey - Cache key for goals
 * @param {(AbortSignal|undefined)} [signal] - Abort signal (optional)
 * @returns {Promise<any[]>} Array of goals (from DB)
 */
async function performFullFetch(userId, cacheKey, signal) {
  logger.info('🔄 goalService.performFullFetch: Starting full fetch from Supabase');
  // simplified: no cooldown bookkeeping

  // FULL FETCH QUERY
  // ===============
  // Fetch ALL goals for this user (no filtering)
  // This establishes the complete baseline for future incremental syncs
  let fullQuery = supabase
    .from('goals')
    // Filter directly by the new goals.user_id column (schema updated)
    .select('*')
    .eq('user_id', userId)
    .eq('is_current', true)
    .order('created_at', { ascending: false });
  if (signal) fullQuery = fullQuery.abortSignal(signal);
  let { data, error } = await fullQuery;
  // Backward-compat: if 'is_current' column is missing on this project, retry without it
  if (error) {
    const msg = String(error?.message || '');
    const code = String(error?.code || '');
    const missingIsCurrent = code === '42703' || code === 'PGRST204' || /is_current/i.test(msg);
    if (missingIsCurrent) {
      let retry = supabase
        .from('goals')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (signal) retry = retry.abortSignal(signal);
      ({ data, error } = await retry);
    }
  }

  if (error) {
    if (isAbortError(error)) {
      logger.debug('⏭️ goalService.performFullFetch: Aborted by signal');
      throw error; // let caller decide how to handle, but avoid noisy error logs here
    }
    logger.error('❌ goalService.performFullFetch: Supabase error:', error);
    throw error;
  }

  logger.info(
    '✅ goalService.performFullFetch: Fetched from Supabase:',
    data?.length || 0,
    'goals',
  );

  // ESTABLISH CACHE BASELINE
  // =======================
  // Store ALL goals in permanent cache with current timestamp
  // Validate with Zod in a non-blocking way.
  const syncTimestamp = new Date().toISOString();
  const validated = Array.isArray(data)
    ? data.map((row) => {
        const res = GoalSchema.safeParse(row);
        if (!res.success) {
          logger.warn('goalService.performFullFetch: Goal validation failed (non-blocking)', {
            id: /** @type {any} */ (row)?.id,
            issues: res.error.issues?.slice(0, 3),
          });
          return row;
        }
        return res.data;
      })
    : [];
  cacheManager.mergeIncremental(cacheKey, validated, userId, syncTimestamp);
  // simplified: no cooldown bookkeeping

  logger.debug(
    '✅ goalService.performFullFetch: Cached',
    data?.length || 0,
    'goals with sync timestamp:',
    syncTimestamp,
  );
  return data || [];
}

/**
 * Create a new goal and add it to permanent cache
 * @param {string} userId - User ID
 * @param {object} goalData - Goal data to create
 * @returns {Promise<any>} Created goal
 */
export async function createGoal(userId, goalData) {
  logger.info('🎯 goalService.createGoal: Creating new goal for user:', userId);
  // Try with canonical 'measurable'; if the DB still has legacy 'measureable', retry
  /** @type {Record<string, any>} */
  let insertPayload = { ...(goalData || {}), user_id: userId };
  // Normalize payload before insert to avoid Postgres type errors and schema mismatches.
  // - Map legacy 'measureable' -> 'measurable' if present
  // - Coerce numeric fields provided as empty strings to null, or to numbers when possible
  if (Object.prototype.hasOwnProperty.call(insertPayload, 'measureable')) {
    if (typeof insertPayload.measurable === 'undefined') {
      insertPayload.measurable = insertPayload.measureable;
    }
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete insertPayload.measureable;
  }
  for (const k of ['baseline_value', 'target_value']) {
    if (Object.prototype.hasOwnProperty.call(insertPayload, k)) {
      const v = insertPayload[k];
      if (v === '' || (typeof v === 'string' && v.trim() === '')) {
        insertPayload[k] = null;
      } else if (typeof v === 'string') {
        const n = Number(v);
        insertPayload[k] = Number.isFinite(n) ? n : null;
      }
    }
  }
  let { data, error } = await supabase.from('goals').insert([insertPayload]).select().single();
  if (error) {
    const msg = String(error?.message || '');
    const code = String(error?.code || '');
    const missingMeasurable =
      code === '42703' ||
      code === 'PGRST204' ||
      /column\s+"?measurable"?\s+does not exist/i.test(msg) ||
      /Could not find the 'measurable' column of 'goals' in the schema cache/i.test(msg);
    if (missingMeasurable && 'measurable' in insertPayload) {
      // Retry using legacy key name 'measureable'
      /** @type {Record<string, any>} */
      const fallback = { ...insertPayload };
      /** @type {any} */ (fallback).measureable = /** @type {any} */ (fallback).measurable;
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete (/** @type {any} */ (fallback).measurable);
      ({ data, error } = await supabase.from('goals').insert([fallback]).select().single());
    }
  }
  if (error) {
    logger.error('❌ goalService.createGoal: Supabase error:', error);
    throw error;
  }

  logger.info('✅ goalService.createGoal: Created goal:', data.id);

  // ADD TO PERMANENT CACHE
  // =====================
  // Add new goal directly to cache instead of clearing it
  // This maintains instant loading while keeping data fresh
  const cacheKey = 'goals';
  const syncTimestamp = new Date().toISOString();
  cacheManager.mergeIncremental(cacheKey, [data], userId, syncTimestamp);

  logger.debug('✅ goalService.createGoal: Added new goal to permanent cache');
  // Best-effort analytics: record goal_created
  try {
    /** @type {Record<string, any>} */
    const client = {
      platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      locale:
        (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
        'en-US',
      app_session_id: getAppSessionId(),
      app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
    };
    if (client.app_version === undefined) delete client.app_version;
    /** @type {Record<string, any>} */
    const meta = {
      entity_type: 'goal',
      entity_id: data.id,
      title: /** @type {any} */ (data)?.title,
      route: typeof window !== 'undefined' ? window.location?.pathname : undefined,
    };
    if (meta.route === undefined) delete meta.route;
    const { error } = await supabase
      .from('user_activity_events')
      .insert([
        {
          user_id: userId,
          event_type: 'goal_created',
          session_id: getUuidSessionId(),
          client,
          meta,
        },
      ])
      .select('id')
      .single();
    if (error) {
      console.warn('[DEBUG] goal_created insert failed:', {
        error,
        details: error.details,
        hint: error.hint,
        message: error.message,
      });
    }
  } catch (err) {
    console.warn('[DEBUG] goal_created exception:', err);
  }

  return data;
}

/**
 * Persist a new order for the user's goals by updating importance_rank.
 * @param {string} userId
 * @param {Array<Record<string, any>>} orderedGoals - Goals in desired order
 * @returns {Promise<any[]>}
 */
export async function reorderGoals(userId, orderedGoals) {
  if (!userId) throw new Error('Missing userId');
  const list = Array.isArray(orderedGoals) ? orderedGoals : [];
  if (list.length === 0) return [];

  const payload = list
    .map((goal, idx) => {
      const id = goal?.id;
      if (!id) return null;
      return {
        id: String(id),
        user_id: userId,
        importance_rank: idx + 1,
      };
    })
    .filter(Boolean);

  if (payload.length === 0) return [];

  logger.info('🎯 goalService.reorderGoals: Updating goal order for user:', userId, {
    count: payload.length,
  });

  /** @type {any[]} */
  const updated = [];
  for (const rec of payload) {
    if (!rec || !rec.id) {
      continue;
    }
    try {
      const { data: row, error: updErr } = await supabase
        .from('goals')
        .update({ importance_rank: rec.importance_rank })
        .eq('id', rec.id)
        .eq('user_id', userId)
        .select()
        .single();
      if (updErr) {
        logger.warn('reorderGoals: per-row update failed', { id: rec.id, err: updErr.message });
        continue;
      }
      if (row) updated.push(row);
    } catch (e) {
      logger.warn('reorderGoals: per-row update exception', { id: rec.id, e });
    }
  }
  const rows = updated;
  const cacheKey = 'goals';
  const syncTimestamp = new Date().toISOString();
  cacheManager.mergeIncremental(cacheKey, rows, userId, syncTimestamp);

  return rows;
}

/**
 * Update an existing goal and refresh it in permanent cache
 * @param {string} userId - User ID
 * @param {string} goalId - Goal ID to update
 * @param {object} updates - Updates to apply
 * @returns {Promise<any>} Updated goal
 */
export async function updateGoal(userId, goalId, updates) {
  logger.info('🎯 goalService.updateGoal: Updating goal:', goalId, 'for user:', userId);
  // Update with canonical 'measurable'; on schema mismatch retry with legacy 'measureable'
  /** @type {Record<string, any>} */
  let upd = { ...(updates || {}) };
  let { data, error } = await supabase
    .from('goals')
    .update(upd)
    .eq('id', goalId)
    .eq('user_id', userId)
    .select()
    .single();
  if (error) {
    const msg = String(error?.message || '');
    const code = String(error?.code || '');
    const missingMeasurable =
      code === '42703' ||
      code === 'PGRST204' ||
      /column\s+"?measurable"?\s+does not exist/i.test(msg) ||
      /Could not find the 'measurable' column of 'goals' in the schema cache/i.test(msg);
    if (missingMeasurable && 'measurable' in upd) {
      /** @type {Record<string, any>} */
      const fallback = { ...upd };
      /** @type {any} */ (fallback).measureable = /** @type {any} */ (fallback).measurable;
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete (/** @type {any} */ (fallback).measurable);
      ({ data, error } = await supabase
        .from('goals')
        .update(fallback)
        .eq('id', goalId)
        .eq('user_id', userId)
        .select()
        .single());
    }
  }
  if (error) {
    logger.error('❌ goalService.updateGoal: Supabase error:', error);
    throw error;
  }

  logger.info('✅ goalService.updateGoal: Updated goal:', data.id);

  // UPDATE IN PERMANENT CACHE
  // ========================
  // Merge updated goal into cache (replaces existing by ID)
  // This ensures cache reflects the latest changes immediately
  const cacheKey = 'goals';
  const syncTimestamp = new Date().toISOString();
  cacheManager.mergeIncremental(cacheKey, [data], userId, syncTimestamp);

  logger.debug('✅ goalService.updateGoal: Updated goal in permanent cache');
  // Best-effort analytics: record goal_updated
  try {
    /** @type {Record<string, any>} */
    const client = {
      platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      locale:
        (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
        'en-US',
      app_session_id: getAppSessionId(),
      app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
    };
    if (client.app_version === undefined) delete client.app_version;
    /** @type {Record<string, any>} */
    const meta = {
      entity_type: 'goal',
      entity_id: data.id,
      title: /** @type {any} */ (data)?.title,
      route: typeof window !== 'undefined' ? window.location?.pathname : undefined,
    };
    if (meta.route === undefined) delete meta.route;
    const { error } = await supabase
      .from('user_activity_events')
      .insert([
        {
          user_id: userId,
          event_type: 'goal_updated',
          session_id: getUuidSessionId(),
          client,
          meta,
        },
      ])
      .select('id')
      .single();
    if (error) {
      console.warn('[DEBUG] goal_updated insert failed:', {
        error,
        details: error.details,
        hint: error.hint,
        message: error.message,
      });
    }
  } catch (err) {
    console.warn('[DEBUG] goal_updated exception:', err);
  }
  return data;
}

/**
 * Delete a goal and remove it from permanent cache
 * @param {string} userId - User ID
 * @param {string} goalId - Goal ID to delete
 * @returns {Promise<boolean>} Success status
 */
export async function deleteGoal(userId, goalId) {
  logger.info('🎯 goalService.deleteGoal: Deleting goal:', goalId, 'for user:', userId);

  // Soft delete: set deleted_at to current timestamp
  const deletedAt = new Date().toISOString();
  const { data: updated, error } = await supabase
    .from('goals')
    .update({ deleted_at: deletedAt })
    .eq('id', goalId)
    .eq('user_id', userId)
    .select('id, deleted_at');

  if (error) {
    logger.error('❌ goalService.deleteGoal: Supabase error:', error);
    throw error;
  }
  if (!updated || updated.length === 0) {
    logger.warn(
      '⚠️ goalService.deleteGoal: No rows updated. Possible user_id mismatch or RLS policy.',
      { userId, goalId },
    );
    throw new Error('Delete failed: no matching goal found or permission denied');
  }

  logger.info('✅ goalService.deleteGoal: Deleted goal from database', updated);

  // REMOVE FROM PERMANENT CACHE (item-level)
  // =======================================
  const cacheKey = 'goals';
  try {
    const prev = /** @type {any[]|null} */ (cacheManager.get(cacheKey, userId));
    const next = Array.isArray(prev) ? prev.filter((g) => String(g?.id) !== String(goalId)) : [];
    cacheManager.set(cacheKey, next, userId);
    logger.debug('✅ goalService.deleteGoal: Removed goal from cache (item-level)');
  } catch (e) {
    logger.warn('goalService.deleteGoal: Failed to remove from cache (non-blocking):', e);
  }
  // Best-effort analytics: record goal_deleted
  try {
    /** @type {Record<string, any>} */
    const client = {
      platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      locale:
        (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
        'en-US',
      app_session_id: getAppSessionId(),
      app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
    };
    if (client.app_version === undefined) delete client.app_version;
    /** @type {Record<string, any>} */
    const meta = {
      entity_type: 'goal',
      entity_id: goalId,
      route: typeof window !== 'undefined' ? window.location?.pathname : undefined,
    };
    if (meta.route === undefined) delete meta.route;
    const { error } = await supabase
      .from('user_activity_events')
      .insert([
        {
          user_id: userId,
          event_type: 'goal_deleted',
          session_id: getUuidSessionId(),
          client,
          meta,
        },
      ])
      .select('id')
      .single();
    if (error) {
      console.warn('[DEBUG] goal_deleted insert failed:', {
        error,
        details: error.details,
        hint: error.hint,
        message: error.message,
      });
    }
  } catch (err) {
    console.warn('[DEBUG] goal_deleted exception:', err);
  }
  return true;
}

// ============================================================================
// PROGRESS TRACKING FUNCTIONS (Added 2025-10-15)
// ============================================================================

/**
 * Log a progress entry for a goal
 *
 * @param {string} goalId - The current goal ID
 * @param {object} entry - Progress entry data
 * @param {string} entry.date - Date in YYYY-MM-DD format
 * @param {number} entry.value - The measured value
 * @param {string} entry.unit - Unit of measurement
 * @param {string} [entry.note] - Optional note
 * @param {string} [entry.source='manual'] - Source: manual, ai_extracted, chat, integration
 * @returns {Promise<any>} The created progress entry
 */
export async function logGoalProgress(goalId, entry) {
  logger.info('📊 goalService.logGoalProgress: Start', { goalId, entry });

  if (!goalId || !entry?.date || typeof entry?.value !== 'number' || !entry?.unit) {
    throw new Error('Missing required fields: goalId, date, value, unit');
  }

  // Fetch the goal to get lineage, version, user_id, and current baseline/target
  const { data: goal, error: fetchError } = await supabase
    .from('goals')
    .select('id, user_id, goal_lineage, goal_version, baseline_value, target_value')
    .eq('id', goalId)
    .single();

  if (fetchError || !goal) {
    logger.error('❌ goalService.logGoalProgress: Failed to fetch goal', fetchError);
    throw fetchError || new Error('Goal not found');
  }

  // Build progress entry payload
  const progressEntry = {
    goal_lineage: goal.goal_lineage || goal.id,
    goal_version: goal.goal_version,
    user_id: goal.user_id,
    logged_date: entry.date,
    value: entry.value,
    unit: entry.unit,
    baseline_value: goal.baseline_value,
    target_value: goal.target_value,
    note: entry.note || null,
    source: entry.source || 'manual',
  };

  // Insert progress entry (trigger will auto-update goal.current_value)
  const { data, error } = await supabase
    .from('goal_progress')
    .insert([progressEntry])
    .select()
    .single();

  if (error) {
    logger.error('❌ goalService.logGoalProgress: Supabase error', error);
    throw error;
  }

  logger.info('✅ goalService.logGoalProgress: Progress logged', data);

  // Clear goals cache to refresh current_value
  cacheManager.clear(goal.user_id, 'goals');

  return data;
}

/**
 * Get progress history for a goal (all versions via lineage)
 *
 * @param {string} goalId - The current goal ID
 * @param {object} [options] - Query options
 * @param {number} [options.limit] - Max entries to return
 * @param {string} [options.startDate] - Filter entries >= this date (YYYY-MM-DD)
 * @param {string} [options.endDate] - Filter entries <= this date (YYYY-MM-DD)
 * @returns {Promise<any[]>} Array of progress entries, sorted by date DESC
 */
export async function getGoalProgressHistory(goalId, options = {}) {
  logger.info('📊 goalService.getGoalProgressHistory: Start', { goalId, options });

  // Fetch the goal to get lineage
  const { data: goal, error: fetchError } = await supabase
    .from('goals')
    .select('goal_lineage, id')
    .eq('id', goalId)
    .single();

  if (fetchError || !goal) {
    logger.error('❌ goalService.getGoalProgressHistory: Failed to fetch goal', fetchError);
    throw fetchError || new Error('Goal not found');
  }

  const lineage = goal.goal_lineage || goal.id;

  // Build query
  let query = supabase
    .from('goal_progress')
    .select('*')
    .eq('goal_lineage', lineage)
    .order('logged_date', { ascending: false });

  // Apply filters
  if (options.startDate) {
    query = query.gte('logged_date', options.startDate);
  }
  if (options.endDate) {
    query = query.lte('logged_date', options.endDate);
  }
  if (options.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;

  if (error) {
    logger.error('❌ goalService.getGoalProgressHistory: Supabase error', error);
    throw error;
  }

  logger.info('✅ goalService.getGoalProgressHistory: Fetched entries', {
    count: data?.length || 0,
  });

  return data || [];
}

/**
 * Update a progress entry
 *
 * @param {string} entryId - Progress entry ID
 * @param {object} updates - Fields to update
 * @param {number} [updates.value] - New value
 * @param {string} [updates.note] - New note
 * @returns {Promise<any>} Updated progress entry
 */
export async function updateGoalProgress(entryId, updates) {
  logger.info('📊 goalService.updateGoalProgress: Start', { entryId, updates });

  if (!entryId) {
    throw new Error('Missing entryId');
  }

  const { data, error } = await supabase
    .from('goal_progress')
    .update(updates)
    .eq('id', entryId)
    .select()
    .single();

  if (error) {
    logger.error('❌ goalService.updateGoalProgress: Supabase error', error);
    throw error;
  }

  logger.info('✅ goalService.updateGoalProgress: Updated entry', data);

  // Clear cache (trigger will recalc current_value)
  if (data?.user_id) {
    cacheManager.clear(data.user_id, 'goals');
  }

  return data;
}

/**
 * Delete a progress entry
 *
 * @param {string} entryId - Progress entry ID
 * @returns {Promise<boolean>} True if deleted
 */
export async function deleteGoalProgress(entryId) {
  logger.info('📊 goalService.deleteGoalProgress: Start', { entryId });

  if (!entryId) {
    throw new Error('Missing entryId');
  }

  // Fetch entry first to get user_id for cache invalidation
  const { data: entry } = await supabase
    .from('goal_progress')
    .select('user_id')
    .eq('id', entryId)
    .single();

  const { error } = await supabase.from('goal_progress').delete().eq('id', entryId);

  if (error) {
    logger.error('❌ goalService.deleteGoalProgress: Supabase error', error);
    throw error;
  }

  logger.info('✅ goalService.deleteGoalProgress: Deleted entry');

  // Clear cache (trigger will recalc current_value)
  if (entry?.user_id) {
    cacheManager.clear(entry.user_id, 'goals');
  }

  return true;
}
